import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import type { ApiApplicationStage } from '../../libs/api/pipelineService';
import { STAGE_LABELS, type PipelineCard } from './kanbanShared';

interface KanbanColumnProps {
  id: ApiApplicationStage;
  candidates: PipelineCard[];
  hasMore: boolean;
  onLoadMore: (stage: ApiApplicationStage) => void;
  renderCard: (candidate: PipelineCard) => React.ReactNode;
  isDropTarget?: boolean;
  onColumnBodyRef?: (stage: ApiApplicationStage, element: HTMLDivElement | null) => void;
}

const getStageColor = (stage: ApiApplicationStage) => {
  switch (stage) {
    case 'NEW':
      return 'bg-muted-foreground';
    case 'SCREENING':
      return 'bg-info';
    case 'INTERVIEW':
      return 'bg-violet-500 dark:bg-violet-400';
    case 'OFFER':
      return 'bg-orange-500 dark:bg-orange-400';
    case 'HIRED':
      return 'bg-success';
    case 'REJECTED':
      return 'bg-destructive';
    default:
      return 'bg-muted-foreground';
  }
};

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  id,
  candidates,
  hasMore,
  onLoadMore,
  renderCard,
  isDropTarget = false,
  onColumnBodyRef,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      type: 'Column',
      stage: id,
    },
  });

  const highlight = isDropTarget || isOver;

  return (
    <div
      className={`group/column flex w-74 min-w-74 shrink-0 flex-col border-r border-border-subtle bg-bg-main/30 transition-colors duration-150 last:border-r-0 min-[1800px]:w-0 min-[1800px]:min-w-60 min-[1800px]:flex-1 ${
        highlight ? 'bg-primary/5' : ''
      }`}
    >
      <div className="sticky top-0 z-10 flex items-center justify-between bg-bg-main/30 p-3.5 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className={`h-1.5 w-1.5 rounded-full ${getStageColor(id)}`} />
          <h3 className="text-[13px] font-bold text-text-main">{STAGE_LABELS[id]}</h3>
        </div>
        <span className="rounded-full border border-border-subtle bg-card px-1.5 py-0.5 text-[11px] font-bold leading-none text-text-subtle shadow-sm">
          {candidates.length}
        </span>
      </div>

      <div
        ref={(node) => {
          setNodeRef(node);
          onColumnBodyRef?.(id, node);
        }}
        className={`relative min-h-125 flex-1 overflow-y-auto p-3 transition-[box-shadow,outline-color] duration-150 ${
          highlight
            ? 'outline -outline-offset-4 outline-primary/40'
            : 'outline -outline-offset-4 outline-transparent'
        }`}
      >
        <SortableContext items={candidates.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {candidates.map((candidate) => renderCard(candidate))}
        </SortableContext>
        {highlight && candidates.length === 0 ? (
          <div className="pointer-events-none mx-1 flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-primary/40 bg-card/40 text-xs font-medium text-primary/80">
            Drop to move to {STAGE_LABELS[id]}
          </div>
        ) : null}
        {hasMore ? (
          <div className="flex justify-center pt-1 pb-2">
            <Button
              type="button"
              onClick={() => onLoadMore(id)}
              variant="outline"
              size="sm"
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-card px-2.5 py-1.5 text-xs font-semibold text-text-main hover:bg-bg-main"
            >
              <ChevronDown size={12} /> Load more
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export type { KanbanColumnProps };
export default KanbanColumn;
